import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { RealtimeChannel } from "@supabase/supabase-js";

const CHANNEL_NAME = "shop-visitors";

const HIDDEN_IPS = ["195.248.254.254", "31.0.37.7"];
const LOVABLE_IP_PREFIXES = ["34.", "35.", "104."];

function isHiddenIP(ip: string | null): boolean {
  if (!ip || ip === "—") return false;
  if (HIDDEN_IPS.includes(ip)) return true;
  // Hide Lovable preview IPs (common GCP/Cloud ranges)
  if (ip.includes("lovable") || ip.includes("preview")) return true;
  return false;
}

function getBrowserInfo() {
  const ua = navigator.userAgent;
  let browser = "Nieznana";
  let os = "Nieznany";

  if (ua.includes("Firefox/")) browser = "Firefox";
  else if (ua.includes("Edg/")) browser = "Edge";
  else if (ua.includes("OPR/") || ua.includes("Opera")) browser = "Opera";
  else if (ua.includes("Chrome/")) browser = "Chrome";
  else if (ua.includes("Safari/") && !ua.includes("Chrome")) browser = "Safari";

  if (ua.includes("Windows")) os = "Windows";
  else if (ua.includes("Mac OS")) os = "macOS";
  else if (ua.includes("Linux") && !ua.includes("Android")) os = "Linux";
  else if (ua.includes("Android")) os = "Android";
  else if (/iPhone|iPad|iPod/.test(ua)) os = "iOS";

  const isMobile = /Mobi|Android|iPhone|iPad|iPod/.test(ua);
  const device = isMobile ? "📱 Mobile" : "💻 Desktop";

  return { browser, os, device };
}

async function fetchIP(): Promise<string> {
  try {
    const res = await fetch("https://api.ipify.org?format=json");
    const data = await res.json();
    return data.ip || "—";
  } catch {
    return "—";
  }
}

/** Public-side: announce presence on a given page AND log visit to DB */
export function useVisitorPresence(pageName: string) {
  const channelRef = useRef<RealtimeChannel | null>(null);
  const loggedRef = useRef(false);

  useEffect(() => {
    const sessionId = getOrCreateSessionId();
    const { browser, os, device } = getBrowserInfo();
    loggedRef.current = false;

    const channel = supabase.channel(CHANNEL_NAME, {
      config: { presence: { key: sessionId } },
    });

    channel
      .on("presence", { event: "sync" }, () => {})
      .subscribe(async (status) => {
        if (status === "SUBSCRIBED") {
          const ip = await fetchIP();
          await channel.track({
            page: pageName,
            ts: Date.now(),
            ip,
            browser,
            os,
            device,
            lang: navigator.language,
            screen: `${screen.width}x${screen.height}`,
          });

          // Log visit to DB (once per page change)
          if (!loggedRef.current && !isHiddenIP(ip)) {
            loggedRef.current = true;
            supabase.from("visitor_logs").insert({
              session_id: sessionId,
              ip,
              page: pageName,
              browser,
              os,
              device,
              lang: navigator.language,
              screen: `${screen.width}x${screen.height}`,
            }).then(() => {});
          }
        }
      });

    channelRef.current = channel;

    return () => {
      channel.untrack();
      supabase.removeChannel(channel);
    };
  }, [pageName]);
}

export interface VisitorInfo {
  page: string;
  ts: number;
  ip: string;
  browser: string;
  os: string;
  device: string;
  lang: string;
  screen: string;
}

/** Admin-side: subscribe and get list of active visitors */
export function useActiveVisitors() {
  const [visitors, setVisitors] = useState<VisitorInfo[]>([]);

  useEffect(() => {
    const channel = supabase.channel(CHANNEL_NAME);

    channel
      .on("presence", { event: "sync" }, () => {
        const state = channel.presenceState<VisitorInfo>();
        const list: VisitorInfo[] = [];
        for (const key of Object.keys(state)) {
          const presences = state[key];
          if (presences && presences.length > 0) {
            const p = presences[0];
            list.push({
              page: p.page,
              ts: p.ts,
              ip: p.ip || "—",
              browser: p.browser || "—",
              os: p.os || "—",
              device: p.device || "—",
              lang: p.lang || "—",
              screen: p.screen || "—",
            });
          }
        }
        setVisitors(list.filter(v => !isHiddenIP(v.ip)));
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return visitors;
}

export interface VisitorLog {
  id: string;
  session_id: string;
  ip: string | null;
  page: string;
  browser: string | null;
  os: string | null;
  device: string | null;
  lang: string | null;
  screen: string | null;
  created_at: string;
}

/** Admin-side: fetch visitor history for last N days */
export function useVisitorHistory(days: number = 3) {
  const [logs, setLogs] = useState<VisitorLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const since = new Date();
      since.setDate(since.getDate() - days);

      const { data } = await supabase
        .from("visitor_logs")
        .select("*")
        .gte("created_at", since.toISOString())
        .order("created_at", { ascending: false })
        .limit(500);

      const allLogs = (data as VisitorLog[]) || [];
      setLogs(allLogs.filter(l => !isHiddenIP(l.ip)));
      setLoading(false);
    }
    load();
  }, [days]);

  return { logs, loading };
}

function getOrCreateSessionId(): string {
  const KEY = "visitor_session_id";
  let id = sessionStorage.getItem(KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(KEY, id);
  }
  return id;
}
